// commentBox.tsx

import { nodataIconDrawer } from '@/shared/ui/assets';
import { NoDataComponent, CustomButton } from '@/shared/ui/components';
import { freshChatWidgetActive } from '@/shared/lib';
import _ from 'lodash';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { t } from 'ttag';
import { useCommentBox, MAX_CHARACTERS } from './useCommentBox';
import { formatTimestamp } from './handler';
import './commentBox.scss';

export interface TicketComment {
  id: string;
  createdBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  content: string;
  action?: string;
}

export interface CommentBoxProps {
  isVisible: boolean;
  studyId: string;
  ticketType?: string;
  isLoadedData?: boolean;
}

const CommentBox: React.FC<CommentBoxProps> = (props) => {
  const { isVisible, isLoadedData = true } = props;

  const {
    comments,
    loading,
    newNote,
    loadingAddNote,
    notesContainerRef,
    textareaRef,
    handleAddNote,
    handleInputChange,
    handlePaste,
    handleKeyPress,
    formatAuthorName,
    characterCount,
    isCharacterLimitReached,
    isAddButtonDisabled,
  } = useCommentBox({
    studyId: props.studyId,
    ticketType: props.ticketType,
    isLoadedData: props.isLoadedData,
  });

  // Render skeleton when data is not loaded
  if (!isLoadedData) {
    return (
      <div className={`comment-box ${isVisible ? '--open' : '--closed'}`} aria-hidden={!isVisible}>
        <section className="notes-container" ref={notesContainerRef}>
          {_.map(_.range(4), (d) => (
            <div key={d} className="note-item">
              <div className="note-header">
                <div className="note-author">
                  <Skeleton width={120} height={22} />
                </div>
              </div>
              <div className="note-content">
                <Skeleton count={2} />
              </div>
              <div className="note-timestamp">
                <Skeleton width={150} height={16} />
              </div>
            </div>
          ))}
        </section>
        <section className="add-note-section">
          <header className="add-note-header">
            <Skeleton width={80} height={22} />
            <Skeleton width={50} height={22} />
          </header>
          <Skeleton height={80} style={{ marginBottom: '8px' }} />
          <div className="note-actions">
            <Skeleton height={25} />
          </div>
        </section>
      </div>
    );
  }

  return (
    <div
      className={`comment-box ${isCharacterLimitReached ? 'limit-reached' : ''} ${freshChatWidgetActive() && 'freshchat'} ${isVisible ? '--open' : '--closed'}`}
      aria-hidden={!isVisible}
    >
      {/* Comments List */}
      <section className="notes-container" ref={notesContainerRef}>
        {loading ? (
          <div className="loading-notes" style={{ textAlign: 'center', padding: '24px' }}>
            <span>Loading comments...</span>
          </div>
        ) : comments.length > 0 ? (
          comments.map((comment) => (
            <article
              key={comment.id}
              className="note-item"
              aria-label={`${t`Comment by`} ${formatAuthorName(comment)}`}
            >
              <header className="note-header">
                <div className="note-author">{formatAuthorName(comment)}</div>
              </header>
              <p className="note-content">{comment.content}</p>
              <time className="note-timestamp" dateTime={comment.createdAt}>
                {formatTimestamp(comment.createdAt)}
              </time>
            </article>
          ))
        ) : (
          <div className="no-data-wrapper">
            <div className="no-data">
              <NoDataComponent
                icon={
                  <img className="no-data__icon" src={nodataIconDrawer} alt={t`No data icon`} />
                }
                isSearching={false}
                message={t`No notes yet. Add one below.`}
              />
            </div>
          </div>
        )}
      </section>

      {/* Add Comment Section */}
      <section className="add-note-section">
        <header className="add-note-header">
          <label className="add-note-label" htmlFor="new-note-input">
            Add notes
          </label>
          <span
            className={`char-count ${characterCount > MAX_CHARACTERS ? '--limit-reached' : ''}`}
          >
            {characterCount}/{MAX_CHARACTERS}
          </span>
        </header>

        <textarea
          ref={textareaRef}
          id="new-note-input"
          name="newNote"
          value={newNote}
          onChange={handleInputChange}
          onPaste={handlePaste}
          onKeyDown={handleKeyPress}
          placeholder="Enter notes..."
          className={`note-input ${isCharacterLimitReached ? '--error' : ''}`}
          rows={3}
          disabled={loadingAddNote}
        />

        {isCharacterLimitReached && (
          <p className="char-limit-warning">Note cannot exceed 200 characters.</p>
        )}

        <div className="note-actions">
          <CustomButton
            id="add-note-btn"
            size="small"
            onClick={handleAddNote}
            block
            loading={loadingAddNote}
            disabled={isAddButtonDisabled}
          >
            Add
          </CustomButton>
        </div>
      </section>
    </div>
  );
};

export default CommentBox;
